import React, { useState } from 'react';
import { Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { notification } from 'antd';
import { createNoteService } from '../../../services/campersServices';
import { removeHtmlTags, resHandle } from '../../../helper';

const escapeHtml = text => String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

// The line prepended to the note so that, read on the user page, it says which booking it came
// from. The booking number links back here. Names are escaped because the note is stored as HTML.
const bookingReferenceHtml = ({ bookingNumber, bookingPath, participantName, programName }) => {
    const booking = bookingPath
        ? `<a href="${escapeHtml(bookingPath)}">${escapeHtml(bookingNumber)}</a>`
        : escapeHtml(bookingNumber);
    const parts = [`Booking ${booking}`, participantName, programName].filter(Boolean)
        .map((part, i) => (i === 0 ? part : escapeHtml(part)));
    return `<p><em>${parts.join(' · ')}</em></p>`;
};

/**
 * "Add Note" from the booking page. Creates the note exactly as the user page's Notes tab does
 * (users/UserNotes.js): same service, same params, same rich-text editor and validation. So the note
 * shows up in that tab alongside the others, starting with a reference line for this booking.
 */
const AddNoteModal = ({ show, onClose, userId, campCode, bookingNumber, bookingPath, participantName, programName }) => {
    const [desc, setDesc] = useState('');
    const [descErr, setDescErr] = useState('');
    const [btnLoader, setBtnLoader] = useState(false);

    const close = () => {
        setDesc('');
        setDescErr('');
        setBtnLoader(false);
        onClose();
    };

    const handleSubmit = () => {
        // removeHtmlTags returns undefined for '' (never-touched editor), hence the fallback.
        if ((removeHtmlTags(desc) || '').trim() === '') {
            setDescErr('Description is required');
            return;
        }

        setBtnLoader(true);
        const reference = bookingNumber
            ? bookingReferenceHtml({ bookingNumber, bookingPath, participantName, programName })
            : '';

        createNoteService({ userId, campCode, desc: reference + desc }).then(res => {
            const { status } = resHandle(res);
            setBtnLoader(false);
            if (status) {
                close();
                notification.open({ message: 'Note', description: 'Note created successfully' });
            }
        }).catch(() => setBtnLoader(false));
    };

    return (
        <Modal size="md" show={show} dialogClassName="remove_header_line" animation={false} centered>
            <Modal.Header>
                <Modal.Title>Add Note</Modal.Title>
                <span className="modal_close" onClick={close}><i className="icon-cross" /></span>
            </Modal.Header>

            <Modal.Body>
                <div className="form-group">
                    <p className="a_label mb-2">Description</p>
                    <ReactQuill
                        className={`inbox_editer ${descErr ? 'is-invalid' : ''}`}
                        theme="snow"
                        value={desc}
                        onChange={e => { setDesc(e); setDescErr(''); }} />

                    {descErr ? <OverlayTrigger placement="left" overlay={<Tooltip>{descErr}</Tooltip>}><i className="input_info" /></OverlayTrigger> : ''}
                </div>

                <p className="text-muted small mb-3">
                    Saved to this user's notes (User Detail → Notes)
                    {bookingNumber ? <>, starting with a link to booking <b>{bookingNumber}</b></> : null}.
                </p>

                <div className="text-center">
                    <button onClick={handleSubmit} style={{ minWidth: '130px' }} disabled={btnLoader} className={`btn btn-primary ps-5 pe-5 ${btnLoader ? 'btnLoader' : ''}`}>Create</button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default AddNoteModal;
